import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getFromStorage, saveToStorage } from '../utils/storage';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';


const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, 
});


apiClient.interceptors.request.use(
  async (config: any) => {
    try {
      const token = await getFromStorage('authToken') || await getFromStorage('access_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);


apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;


    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = await getFromStorage('refreshToken') || await getFromStorage('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`, {
            refresh_token: refreshToken,
          });

          if (response.data.access_token) {
            await saveToStorage('authToken', response.data.access_token);
            await saveToStorage('access_token', response.data.access_token);
            if (response.data.refresh_token) {
              await saveToStorage('refreshToken', response.data.refresh_token);
              await saveToStorage('refresh_token', response.data.refresh_token);
            }
            originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
            return apiClient(originalRequest);
          }
        }
      } catch (refreshError) {

        await localStorage.removeItem('authToken');
        await localStorage.removeItem('access_token');
        await localStorage.removeItem('refreshToken');
        await localStorage.removeItem('refresh_token');
        await localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);


export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => 
    apiClient.get(url, config).then(res => res.data),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const requestConfig = {
      ...config,
      timeout: config?.timeout || (url.includes('/rides') ? 90000 : 30000),
    };
    return apiClient.post(url, data, requestConfig).then(res => res.data);
  },

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => 
    apiClient.put(url, data, config).then(res => res.data),

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => 
    apiClient.delete(url, config).then(res => res.data),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => 
    apiClient.patch(url, data, config).then(res => res.data),
};


export const authAPI = {
  login: (email: string, password: string) => 
    api.post<{ user: any; access_token: string; refresh_token: string }>(API_ENDPOINTS.AUTH.LOGIN, {
      identifier: email,
      password,
    }),

  register: (userData: any) => 
    api.post<{ user: any; access_token: string; refresh_token: string }>(API_ENDPOINTS.AUTH.REGISTER, {
      ...userData,
      role: 'rider',
    }),

  registerDriver: (driverData: any) =>
    api.post<{ user: any; access_token: string; refresh_token: string }>(API_ENDPOINTS.AUTH.REGISTER_DRIVER, driverData),

  logout: () => 
    api.post<{ success: boolean }>(API_ENDPOINTS.AUTH.LOGOUT),

  refreshToken: (refreshToken: string) => 
    api.post<{ access_token: string; refresh_token?: string }>(API_ENDPOINTS.AUTH.REFRESH, {
      refresh_token: refreshToken,
    }),

  verifyPhone: (phone: string, code: string) =>
    api.post<{ success: boolean }>(API_ENDPOINTS.AUTH.VERIFY_PHONE, { phone, code }),

  verifyEmail: (email: string, code: string) =>
    api.post<{ success: boolean }>(API_ENDPOINTS.AUTH.VERIFY_EMAIL, { email, code }),

  resendVerification: (type: 'phone' | 'email') =>
    api.post<{ success: boolean }>(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, { type }),

  getVerificationStatus: () =>
    api.get<{ phone_verified: boolean; email_verified: boolean }>(API_ENDPOINTS.AUTH.VERIFICATION_STATUS),

  forgotPassword: (email: string) => 
    api.post<{ success: boolean; message?: string }>('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) => 
    api.post<{ success: boolean; message?: string }>('/auth/reset-password', { token, password }),
};


export const userAPI = {
  getProfile: () => 
    api.get<{ success: boolean; data?: any }>(API_ENDPOINTS.USERS.PROFILE),

  updateProfile: (userData: any) => 
    api.put<{ success: boolean; data?: any; message?: string }>(API_ENDPOINTS.USERS.UPDATE_PROFILE, userData),

  updateLocation: (location: { latitude: number; longitude: number }) =>
    api.put<{ success: boolean }>(API_ENDPOINTS.USERS.UPDATE_LOCATION, location),

  updateAvatar: (avatarData: FormData) => 
    api.post<{ success: boolean; data?: any; message?: string }>('/user/avatar', avatarData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  getPaymentMethods: async () => {
    try {
      return await api.get<{ success: boolean; data?: any[] }>(API_ENDPOINTS.PAYMENTS.METHODS);
    } catch (error: any) {

      if (error?.response?.status === 404) {
        return { success: true, data: [] };
      }
      throw error;
    }
  },

  addPaymentMethod: (methodData: any) => 
    api.post<{ success: boolean; data?: any; message?: string }>(API_ENDPOINTS.PAYMENTS.METHODS, methodData),

  deletePaymentMethod: (methodId: string) => 
    api.delete<{ success: boolean; message?: string }>(`${API_ENDPOINTS.PAYMENTS.METHODS}/${methodId}`),

  getWalletBalance: () =>
    api.get<{ success: boolean; balance?: number }>(API_ENDPOINTS.PAYMENTS.WALLET),

  getWalletHistory: (page = 1, limit = 10) =>
    api.get<{ success: boolean; data?: any[] }>(`${API_ENDPOINTS.PAYMENTS.WALLET_HISTORY}?page=${page}&limit=${limit}`),

  addMoneyToWallet: (amount: number, paymentMethodId: string) =>
    api.post<{ success: boolean }>(API_ENDPOINTS.PAYMENTS.ADD_MONEY, { amount, payment_method_id: paymentMethodId }),

  getTransactions: (page = 1, limit = 10) =>
    api.get<{ success: boolean; data?: any[] }>(`${API_ENDPOINTS.PAYMENTS.TRANSACTIONS}?page=${page}&limit=${limit}`),

  changePassword: (passwordData: { currentPassword: string; newPassword: string }) =>
    api.post<{ success: boolean; message?: string }>('/users/change-password', passwordData),
};


export const rideAPI = {
  createRide: (rideData: any) => 
    api.post<any>(API_ENDPOINTS.RIDES.CREATE, rideData),

  getActiveRide: () => 
    api.get<any>(API_ENDPOINTS.RIDES.CURRENT),

  getRideHistory: (page = 1, limit = 10) => 
    api.get<any>(`${API_ENDPOINTS.RIDES.HISTORY}?page=${page}&limit=${limit}`),

  getRideDetails: (rideId: string) => 
    api.get<any>(API_ENDPOINTS.RIDES.DETAIL.replace(':id', rideId)),

  cancelRide: (rideId: string, reason?: string) => 
    api.post<any>(API_ENDPOINTS.RIDES.CANCEL.replace(':id', rideId), { reason }),

  rateRide: (rideId: string, ratedUserId: string, ratingType: 'driver' | 'rider', rating: number) => 
    api.post<any>(API_ENDPOINTS.RATINGS.CREATE, { 
      rideId: parseInt(rideId, 10), 
      ratedUserId: parseInt(ratedUserId, 10), 
      ratingType, 
      rating,
    }),

  trackRide: (rideId: string) => 
    api.get<any>(API_ENDPOINTS.RIDES.DETAIL.replace(':id', rideId)),

  estimateFare: (data: any) =>
    api.post<any>(API_ENDPOINTS.RIDES.ESTIMATE, data),

  acceptRide: (rideId: string) =>
    api.post<any>(API_ENDPOINTS.RIDES.ACCEPT.replace(':id', rideId)),

  startRide: (rideId: string) =>
    api.post<any>(API_ENDPOINTS.RIDES.START.replace(':id', rideId)),

  completeRide: (rideId: string, finalFare?: number) =>
    api.post<any>(API_ENDPOINTS.RIDES.COMPLETE.replace(':id', rideId), { final_fare: finalFare }),

  arriveRide: (rideId: string) =>
    api.post<any>(API_ENDPOINTS.RIDES.ARRIVE.replace(':id', rideId)),
};


export const driverAPI = {
  updateStatus: (status: 'online' | 'offline' | 'busy') =>
    api.put<{ success: boolean }>(API_ENDPOINTS.DRIVERS.STATUS, { status }),

  updateLocation: (location: { latitude: number; longitude: number }) =>
    api.put<{ success: boolean }>(API_ENDPOINTS.DRIVERS.LOCATION, location),

  getVehicles: () =>
    api.get<{ success: boolean; data?: any[] }>(API_ENDPOINTS.DRIVERS.VEHICLES),

  getEarnings: (startDate?: string, endDate?: string) =>
    api.get<{ success: boolean; data?: any }>(`${API_ENDPOINTS.DRIVERS.EARNINGS}?start_date=${startDate || ''}&end_date=${endDate || ''}`),

  getStats: () =>
    api.get<{ success: boolean; data?: any }>(API_ENDPOINTS.DRIVERS.STATS),
};


export const locationAPI = {
  autocomplete: (input: string) => 
    api.get<{ success: boolean; data?: any[] }>(`/location/autocomplete?input=${encodeURIComponent(input)}`),

  geocode: (placeId: string) => 
    api.get<{ success: boolean; data?: any }>(`/location/geocode?place_id=${placeId}`),

  reverseGeocode: (lat: number, lng: number) => 
    api.get<{ success: boolean; data?: any }>(`/location/reverse-geocode?lat=${lat}&lng=${lng}`),

  getEstimatedFare: (origin: { lat: number; lng: number }, destination: { lat: number; lng: number }) => 
    api.post<{ success: boolean; data?: any; message?: string }>('/location/estimate-fare', {
      origin,
      destination,
    }),
};


export const vehicleTypesAPI = {
  getVehicleTypes: () => 
    api.get<any>(API_ENDPOINTS.VEHICLE_TYPES.LIST),
};


export const servicesAPI = {
  getServices: () =>
    api.get<{ success: boolean; data?: any[] }>(API_ENDPOINTS.SERVICES.LIST),

  getSuggestedDestinations: (limit = 10) =>
    api.get<{ success: boolean; data?: any[] }>(`${API_ENDPOINTS.SERVICES.SUGGESTED_DESTINATIONS}?limit=${limit}`),
};


export const ratingsAPI = {
  createRating: (rideId: string, ratedUserId: string, ratingType: 'driver' | 'rider', rating: number) =>
    api.post<{ success: boolean }>(API_ENDPOINTS.RATINGS.CREATE, { 
      rideId: parseInt(rideId, 10), 
      ratedUserId: parseInt(ratedUserId, 10), 
      ratingType, 
      rating,
    }),

  getRatings: (page = 1, limit = 10) =>
    api.get<{ success: boolean; data?: any[] }>(`${API_ENDPOINTS.RATINGS.LIST}?page=${page}&limit=${limit}`),
};


export const chatAPI = {
  getConversations: () =>
    api.get<{ success: boolean; data?: any[] }>(API_ENDPOINTS.CHAT.CONVERSATIONS),

  getMessages: (conversationId: string, page = 1, limit = 50) =>
    api.get<{ success: boolean; data?: any[] }>(API_ENDPOINTS.CHAT.MESSAGES.replace(':id', conversationId) + `?page=${page}&limit=${limit}`),

  sendMessage: (conversationId: string, message: string) =>
    api.post<{ success: boolean }>(API_ENDPOINTS.CHAT.SEND.replace(':id', conversationId), { message }),
};


export const sosAPI = {
  createSOS: (location: { latitude: number; longitude: number }, message?: string) =>
    api.post<{ success: boolean }>(API_ENDPOINTS.SOS.CREATE, { location, message }),

  getSOSHistory: () =>
    api.get<{ success: boolean; data?: any[] }>(API_ENDPOINTS.SOS.LIST),
};

export default api;
